import React, { useMemo } from 'react';
import { X, Plus } from 'lucide-react';
import { INPUT, LABEL, STRUCTURAL_TYPES } from '../../lib/formConstants';
import { normalizeShowWhen } from '../../lib/fieldConditions';

/**
 * ShowWhenEditor — AND/OR condition-group editor for the v2 show_when schema.
 *
 * Replaces the old 3-op editor that lived inline in FieldInspector. Writes
 * v2 `{ match, conditions: [{ field_id, op, value, value_type }] }` via the
 * same `onUpdate({ show_when })` partial-merge contract. Legacy
 * `{ field_id, op, value }` is normalized for display and persisted as v2 on
 * the first edit.
 *
 * Props:
 *   field     — the field being edited (has field_id, show_when, field_type)
 *   allFields — flattened list from FormBuilder (carries step context)
 *   onUpdate  — FieldInspector's partial-merge callback
 */

const OPS = {
    string: [
        ['equals', 'equals'], ['not_equals', 'not equals'],
        ['contains', 'contains'], ['not_contains', 'does not contain'],
        ['starts_with', 'starts with'], ['ends_with', 'ends with'],
        ['is_empty', 'is empty'], ['is_not_empty', 'is not empty'],
    ],
    number: [
        ['equals', '='], ['not_equals', '≠'], ['greater_than', '>'], ['less_than', '<'],
        ['greater_equal', '≥'], ['less_equal', '≤'],
        ['is_empty', 'is empty'], ['is_not_empty', 'is not empty'],
    ],
    count: [
        ['equals', 'selected count ='], ['not_equals', 'selected count ≠'],
        ['greater_than', 'selected count >'], ['less_than', 'selected count <'],
        ['greater_equal', 'selected count ≥'], ['less_equal', 'selected count ≤'],
    ],
};
const UNARY_OPS = new Set(['is_empty', 'is_not_empty']);
const defaultValueType = (f) =>
    f?.field_type === 'number' ? 'number' : f?.field_type === 'multi_select' ? 'count' : 'string';

// Ensure the operator is valid for the given value type; fall back to 'equals'.
const validOpForType = (op, vt) =>
    (OPS[vt] || OPS.string).some(([o]) => o === op) ? op : 'equals';

export default function ShowWhenEditor({ field, allFields, onUpdate }) {
    // Normalize legacy {field_id, op, value} → v2 {match, conditions} for
    // display. v2 passes through. The actual field.show_when stays legacy
    // until the first edit writes v2 via commit().
    const sw = useMemo(() => normalizeShowWhen(field.show_when), [field.show_when]);

    // Lookup of all fields by id — resolves trigger fields and detects
    // stale (deleted) triggers.
    const fieldsById = useMemo(() => {
        const map = {};
        allFields.forEach(f => { if (f.field_id) map[f.field_id] = f; });
        return map;
    }, [allFields]);

    // Eligible trigger fields: exclude self, exclude structural types,
    // and order-restrict to fields preceding the current field (a condition
    // can only reference fields the respondent has already answered).
    const triggerFields = useMemo(() => {
        const myIdx = allFields.findIndex(f => f.field_id === field.field_id);
        return allFields.filter((f, i) =>
            f.field_id !== field.field_id &&
            !STRUCTURAL_TYPES.includes(f.field_type) &&
            (myIdx < 0 || i < myIdx)
        );
    }, [allFields, field.field_id]);

    const conditions = sw?.conditions || [];
    const match = sw?.match || 'all';

    // Commit the full v2 show_when. Empty conditions → null (always visible).
    const commit = (newConditions, newMatch = match) => {
        const filtered = newConditions.filter(c => c && c.field_id);
        onUpdate({ show_when: filtered.length ? { match: newMatch, conditions: filtered } : null });
    };

    const setMatch = (newMatch) => commit(conditions, newMatch);

    // Pre-select the first available trigger so the new condition survives
    // normalizeShowWhen's empty-field_id filter on the next render.
    const addCondition = () => {
        if (!triggerFields.length) return;
        const first = triggerFields[0];
        const vt = defaultValueType(first);
        commit([...conditions, { field_id: first.field_id, op: 'equals', value: '', value_type: vt }]);
    };

    const removeCondition = (idx) => {
        commit(conditions.filter((_, i) => i !== idx));
    };

    const updateCondition = (idx, patch) => {
        commit(conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
    };

    // When the trigger field changes, reset value_type to the default for
    // that field, reset the operator to a valid one, and clear the value.
    const onTriggerChange = (idx, fieldId) => {
        const trigger = fieldsById[fieldId];
        const vt = defaultValueType(trigger);
        updateCondition(idx, {
            field_id: fieldId,
            value_type: vt,
            op: validOpForType(conditions[idx].op, vt),
            value: '',
        });
    };

    // When value_type changes, reset the operator if invalid for the new type.
    const onValueTypeChange = (idx, vt) => {
        updateCondition(idx, {
            value_type: vt,
            op: validOpForType(conditions[idx].op, vt),
        });
    };

    return (
        <div className="space-y-2">
            {conditions.length > 1 && (
                <div>
                    <label className={LABEL}>Match logic</label>
                    <select value={match} onChange={e => setMatch(e.target.value)} className={INPUT}>
                        <option value="all">All conditions (AND)</option>
                        <option value="any">Any condition (OR)</option>
                    </select>
                </div>
            )}

            {conditions.map((cond, idx) => {
                const trigger = fieldsById[cond.field_id];
                const isStale = cond.field_id && !trigger;
                const vt = cond.value_type || 'string';
                const ops = OPS[vt] || OPS.string;
                const isUnary = UNARY_OPS.has(cond.op);
                const triggerOptions = trigger?.options?.filter(Boolean) || [];
                const inTriggerList = triggerFields.some(f => f.field_id === cond.field_id);

                return (
                    <div
                        key={idx}
                        className={`space-y-1.5 rounded-lg p-2 ${isStale ? 'border border-red-200 bg-red-50' : 'border border-stone-100'}`}
                    >
                        <div className="flex items-start gap-1.5">
                            <select
                                value={cond.field_id}
                                onChange={e => onTriggerChange(idx, e.target.value)}
                                className={INPUT}
                            >
                                {triggerFields.map(f => (
                                    <option key={f.field_id} value={f.field_id}>
                                        {f.label || 'Untitled'} ({f.field_type})
                                    </option>
                                ))}
                                {/* Show the current trigger if it exists but is no
                                    longer in the eligible list (reordered after
                                    this field). */}
                                {!inTriggerList && cond.field_id && trigger && (
                                    <option value={cond.field_id}>
                                        {trigger.label || 'Untitled'} ({trigger.field_type})
                                    </option>
                                )}
                                {/* Deleted trigger — flagged in red below. */}
                                {isStale && (
                                    <option value={cond.field_id}>⚠ Deleted field</option>
                                )}
                            </select>
                            <button
                                type="button"
                                onClick={() => removeCondition(idx)}
                                className="mt-1 p-1 text-stone-400 hover:text-red-500 flex-shrink-0"
                                title="Remove condition"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {isStale && (
                            <p className="text-[10px] text-red-500">
                                This field was deleted. Remove this condition.
                            </p>
                        )}

                        {!isStale && cond.field_id && (
                            <div className="flex gap-1.5">
                                <select
                                    value={vt}
                                    onChange={e => onValueTypeChange(idx, e.target.value)}
                                    className={INPUT}
                                >
                                    <option value="string">String</option>
                                    <option value="number">Number</option>
                                    <option value="count">Count</option>
                                </select>
                                <select
                                    value={cond.op}
                                    onChange={e => updateCondition(idx, { op: e.target.value })}
                                    className={INPUT}
                                >
                                    {ops.map(([op, label]) => (
                                        <option key={op} value={op}>{label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {!isStale && cond.field_id && !isUnary && (
                            vt === 'number' ? (
                                <input
                                    type="number"
                                    value={cond.value ?? ''}
                                    onChange={e => updateCondition(idx, { value: e.target.value })}
                                    className={INPUT}
                                    placeholder="Number…"
                                />
                            ) : vt === 'count' ? (
                                <input
                                    type="number" min="0" step="1"
                                    value={cond.value ?? ''}
                                    onChange={e => updateCondition(idx, { value: e.target.value })}
                                    className={INPUT}
                                    placeholder="Count…"
                                />
                            ) : (
                                <>
                                    <input
                                        type="text"
                                        value={cond.value ?? ''}
                                        onChange={e => updateCondition(idx, { value: e.target.value })}
                                        className={INPUT}
                                        placeholder="Value…"
                                        list={`sw-opts-${field.field_id}-${idx}`}
                                    />
                                    {triggerOptions.length > 0 && (
                                        <datalist id={`sw-opts-${field.field_id}-${idx}`}>
                                            {triggerOptions.map(o => (
                                                <option key={o} value={o} />
                                            ))}
                                        </datalist>
                                    )}
                                </>
                            )
                        )}
                    </div>
                );
            })}

            <button
                type="button"
                onClick={addCondition}
                disabled={!triggerFields.length}
                className="w-full flex items-center justify-center gap-1 text-xs text-stone-500 hover:text-stone-700 border border-dashed border-stone-200 rounded-lg py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
                <Plus className="w-3.5 h-3.5" />
                {triggerFields.length ? 'Add condition' : 'No preceding fields to condition on'}
            </button>
        </div>
    );
}
